import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChatPanel } from "@/components/ChatPanel";
import { cn } from "@/lib/utils";
import { MessageSquare, Maximize2 } from "lucide-react";

export const ChatBubble = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["chat-unread"],
    queryFn: async () => (await api.get("/chat/unread")).data,
    refetchInterval: 8000,
  });
  const unread = data?.unread || 0;
  const priority = data?.priority || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("relative text-slate-500 hover:text-navy", priority > 0 && "text-gold-dark")} data-testid="chat-bubble">
          {priority > 0 && (
            <span className="pointer-events-none absolute inset-0 rounded-full bg-gold/30 animate-ping" data-testid="chat-pulse" />
          )}
          <MessageSquare className={cn("h-5 w-5 relative", priority > 0 && "fill-gold/20")} />
          {unread > 0 && (
            <span className={cn(
              "absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold",
              priority > 0 ? "bg-gold text-navy ring-2 ring-white" : "bg-gold text-navy"
            )} data-testid="chat-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 overflow-hidden" data-testid="chat-popover">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="font-heading text-sm font-semibold text-navy">Team Chat</span>
          <button
            onClick={() => { setOpen(false); navigate("/chat"); }}
            data-testid="chat-open-full"
            className="inline-flex items-center gap-1 text-xs text-gold-dark hover:underline"
          >
            <Maximize2 className="h-3.5 w-3.5" /> Open full
          </button>
        </div>
        <ChatPanel compact />
      </PopoverContent>
    </Popover>
  );
};
